/* global SENTRY_PUBLIC_DSN, SENTRY_USER, SENTRY_SPLIT_TAGS */
import * as Sentry from '@sentry/browser'

const INJECTED_SCRIPT_PARSE_ERRORS = [
  'No identifiers allowed directly after numeric literal',
  'Non-number found after exponent indicator',
  'No binary digits after \'0b\''
]

// iOS WebKit reports these at line 1 of the page URL when the browser/app
// injects script into the page. Drop them only when no frame is from our
// packs, so a parse error in our own bundle still gets reported. See
// VINTERIOR-FRONTEND-70
export function isInjectedScriptParseError (event) {
  const values = event.exception?.values || []
  const exception = values[values.length - 1]
  const frames = exception?.stacktrace?.frames || []
  const isParseError = INJECTED_SCRIPT_PARSE_ERRORS.some(message => exception?.value?.includes(message))

  return isParseError && !frames.some(frame => frame.filename?.includes('/packs/'))
}

export default function () {
  // NOTE: These are lists compiled by the community and suggested by sentry in
  // https://docs.sentry.io/platforms/javascript/#decluttering-sentry
  const ignoreErrors = [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    'Can\'t find variable: ZiteReader',
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
    // reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Facebook/Instagram iOS in-app browsers inject scripts that reference
    // their own handlers. See VINTERIOR-FRONTEND-12 and -DAJ8
    '_AutofillCallbackHandler',
    'window.webkit.messageHandlers',
    // Safari/DuckDuckGo browser extensions. See VINTERIOR-FRONTEND-8NND
    'Invalid call to runtime.sendMessage(). Tab not found.',
    'Non-Error exception captured',
    'Non-Error promise rejection captured'
  ]

  const denyUrls = [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
  ]

  const allowUrls = [
    /vinterior\.co/,
    /vinterior-app\.herokuapp\.com/,
    /vinterior-beta\.herokuapp\.com/,
    /cdn\.jsdelivr\.net/
  ]

  const sentryOptions = {
    dsn: SENTRY_PUBLIC_DSN,
    environment: process.env.NODE_ENV,
    ignoreErrors,
    denyUrls,
    allowUrls: (process.env.NODE_ENV === 'production') ? allowUrls : [],
    beforeSend: event => isInjectedScriptParseError(event) ? null : event
  }

  Sentry.init(sentryOptions)
  Sentry.setUser(SENTRY_USER)
  Sentry.setTags(SENTRY_SPLIT_TAGS)

  window.Sentry = Sentry
}
